import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { SecondaryButton } from '../components/SecondaryButton';
import { Footer } from '../components/Footer';
import { ConfirmModal } from '../components/ConfirmModal';
import { useCustomerAuth, RequireCustomer } from '../../lib/use-customer-auth';
import {
  getMe,
  exportMyData,
  deleteMyAccount,
} from '../../lib/customer-api';
import { clearCustomerAuth } from '../../lib/customer-auth';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

interface SettingsCardProps {
  icon: string | React.ReactNode;
  title: string;
  description: string;
  isMaterialIcon?: boolean;
  showArrow?: boolean;
  to?: string;
  onClick?: () => void;
  className?: string;
}

function SettingsCard({ icon, title, description, isMaterialIcon = true, showArrow = false, to, onClick, className = '' }: SettingsCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (to) {
      navigate(to);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`bg-white rounded-lg p-6 flex items-start gap-4 cursor-pointer shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] hover:shadow-[0px_20px_24px_0px_rgba(153,155,168,0.15)] transition-all border border-transparent hover:border-transparent group ${className}`}
    >
      <div className="w-[48px] h-[48px] rounded-lg shrink-0 flex items-center justify-center overflow-hidden">
        {isMaterialIcon && typeof icon === 'string' ? (
          <div className="w-full h-full rounded-lg flex items-center justify-center" style={{ backgroundImage: "linear-gradient(90deg, rgba(58, 51, 53, 0.04) 0%, rgba(58, 51, 53, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)" }}>
            <span className="material-icons text-[#908e8e] text-[22px]">{icon}</span>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">{icon}</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-base mb-1">{title}</h3>
        <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] tracking-[-0.1px] truncate whitespace-nowrap">{description}</p>
      </div>
      <span className={`material-icons text-[#8e2a2f] text-[22px] self-center transition-all duration-200 opacity-0 translate-x-[-10px] group-hover:opacity-100 group-hover:translate-x-0`}>
        arrow_forward
      </span>
    </div>
  );
}

export default function AccountSettingsPage() {
  return (
    <RequireCustomer>
      <AccountSettingsInner />
    </RequireCustomer>
  );
}

function AccountSettingsInner() {
  const navigate = useNavigate();
  const { user } = useCustomerAuth();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  // The JWT-cached `user` only has id/name/email — the real avatar
  // URL lives on customer.user.profile_pic. Pull it from /customer/me
  // on mount so we can show the customer's actual photo instead of a
  // placeholder.
  const [profilePic, setProfilePic] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((res) => {
        if (cancelled) return;
        const pic = res?.info?.user?.profile_pic ?? null;
        setProfilePic(pic);
      })
      .catch(() => {
        // Non-fatal — falls through to the initial-letter avatar.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
    'Your account';
  const displayEmail = user?.email ?? '';

  // GDPR Article 20 — download a JSON copy of the customer's data.
  const handleExportData = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await exportMyData();
      const blob = new Blob([JSON.stringify(res.info ?? {}, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cherry-picked-my-data.json';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Your data has been downloaded.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not export your data.'));
    } finally {
      setExporting(false);
    }
  };

  // GDPR Article 17 — irreversible account deletion + PII erasure.
  const performDelete = async () => {
    setDeleting(true);
    try {
      await deleteMyAccount();
      clearCustomerAuth();
      setDeleteOpen(false);
      toast.success('Your account has been deleted.');
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not delete your account.'));
    } finally {
      setDeleting(false);
    }
  };

  const settingsCards: SettingsCardProps[] = [
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="group">
          <g clipPath="url(#clip0_125_3549)">
            <rect width="48" height="48" rx="8" fill="white" />
            <rect
              width="48" height="48" rx="8"
              fill="#3A3335"
              fillOpacity="0.04"
              className="group-hover:fill-[#8e2a2f] group-hover:fill-opacity-10 transition-all duration-300"
            />
            <path
              d="M20.9062 29.7578C21.9948 30.3307 23.026 30.6172 24 30.6172C24.974 30.6172 25.9909 30.3307 27.0508 29.7578C28.1393 29.1849 28.9557 28.4831 29.5 27.6523C29.4714 26.8503 28.8268 26.1771 27.5664 25.6328C26.306 25.0885 25.1172 24.8164 24 24.8164C22.8828 24.8164 21.694 25.0885 20.4336 25.6328C19.1732 26.1484 18.5286 26.8216 18.5 27.6523C19.0443 28.4831 19.8464 29.1849 20.9062 29.7578ZM25.9336 18.4141C25.3893 17.8698 24.7448 17.5977 24 17.5977C23.2552 17.5977 22.6107 17.8698 22.0664 18.4141C21.5221 18.9583 21.25 19.6029 21.25 20.3477C21.25 21.0924 21.5221 21.737 22.0664 22.2812C22.6107 22.8255 23.2552 23.0977 24 23.0977C24.7448 23.0977 25.3893 22.8255 25.9336 22.2812C26.4779 21.737 26.75 21.0924 26.75 20.3477C26.75 19.6029 26.4779 18.9583 25.9336 18.4141ZM17.5117 17.5547C19.3164 15.75 21.4792 14.8477 24 14.8477C26.5208 14.8477 28.6693 15.75 30.4453 17.5547C32.25 19.3307 33.1523 21.4792 33.1523 24C33.1523 26.5208 32.25 28.6836 30.4453 30.4883C28.6693 32.2643 26.5208 33.1523 24 33.1523C21.4792 33.1523 19.3164 32.2643 17.5117 30.4883C15.7357 28.6836 14.8477 26.5208 14.8477 24C14.8477 21.4792 15.7357 19.3307 17.5117 17.5547Z"
              fill="#908E8E"
              className="group-hover:fill-[#8e2a2f] transition-colors duration-300"
            />
          </g>
          <defs>
            <clipPath id="clip0_125_3549">
              <rect width="48" height="48" fill="white" />
            </clipPath>
          </defs>
        </svg>
      ),
      isMaterialIcon: false,
      onClick: () => navigate('/account-info'),
      title: 'Account information',
      description: 'Profile photo, name & language',
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="group">
          <g clipPath="url(#clip0_125_3556)">
            <rect width="48" height="48" rx="8" fill="white" />
            <rect
              width="48" height="48" rx="8"
              fill="#3A3335"
              fillOpacity="0.04"
              className="group-hover:fill-[#8e2a2f] group-hover:fill-opacity-10 transition-all duration-300"
            />
            <path
              d="M16.6667 16.667C16.1804 16.667 15.7141 16.8601 15.3703 17.204C15.0265 17.5478 14.8333 18.0141 14.8333 18.5003V22.167C15.3196 22.167 15.7859 22.3601 16.1297 22.704C16.4735 23.0478 16.6667 23.5141 16.6667 24.0003C16.6667 24.4866 16.4735 24.9529 16.1297 25.2967C15.7859 25.6405 15.3196 25.8337 14.8333 25.8337V29.5003C14.8333 29.9866 15.0265 30.4529 15.3703 30.7967C15.7141 31.1405 16.1804 31.3337 16.6667 31.3337H31.3333C31.8196 31.3337 32.2859 31.1405 32.6297 30.7967C32.9735 30.4529 33.1667 29.9866 33.1667 29.5003V25.8337C32.6804 25.8337 32.2141 25.6405 31.8703 25.2967C31.5265 24.9529 31.3333 24.4866 31.3333 24.0003C31.3333 23.5141 31.5265 23.0478 31.8703 22.704C32.2141 22.3601 32.6804 22.167 33.1667 22.167V18.5003C33.1667 18.0141 32.9735 17.5478 32.6297 17.204C32.2859 16.8601 31.8196 16.667 31.3333 16.667H16.6667ZM27.2083 19.417L28.5833 20.792L20.7917 28.5837L19.4167 27.2087L27.2083 19.417ZM21.0758 19.4537C21.9742 19.4537 22.6983 20.1778 22.6983 21.0762C22.6983 21.5065 22.5274 21.9192 22.2231 22.2234C21.9188 22.5277 21.5062 22.6987 21.0758 22.6987C20.1775 22.6987 19.4533 21.9745 19.4533 21.0762C19.4533 20.6458 19.6243 20.2332 19.9286 19.9289C20.2328 19.6246 20.6455 19.4537 21.0758 19.4537ZM26.9242 25.302C27.8225 25.302 28.5467 26.0262 28.5467 26.9245C28.5467 27.3548 28.3757 27.7675 28.0715 28.0718C27.7672 28.3761 27.3545 28.547 26.9242 28.547C26.0258 28.547 25.3017 27.8228 25.3017 26.9245C25.3017 26.4942 25.4726 26.0815 25.7769 25.7772C26.0812 25.4729 26.4939 25.302 26.9242 25.302Z"
              fill="#908E8E"
              className="group-hover:fill-[#8e2a2f] transition-colors duration-300"
            />
          </g>
          <defs>
            <clipPath id="clip0_125_3556">
              <rect width="48" height="48" fill="white" />
            </clipPath>
          </defs>
        </svg>
      ),
      isMaterialIcon: false,
      onClick: () => navigate('/vouchers'),
      title: 'Vouchers',
      description: 'Exclusive discounts',
    },
    {
      icon: 'account_balance_wallet',
      isMaterialIcon: true,
      onClick: () => navigate('/wallet'),
      title: 'Wallet',
      description: 'Gift card balance & activity',
    },

    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="group">
          <g clipPath="url(#clip0_125_3578)">
            <rect width="48" height="48" rx="8" fill="white" />
            <rect
              width="48" height="48" rx="8"
              fill="#3A3335"
              fillOpacity="0.04"
              className="group-hover:fill-[#8e2a2f] group-hover:fill-opacity-10 transition-all duration-300"
            />
            <path
              d="M33.1523 28.5977V22.1523H29.5V28.5977H33.1523ZM34.0977 20.3477C34.3555 20.3477 34.5703 20.4336 34.7422 20.6055C34.9141 20.7773 35 20.9922 35 21.25V30.4023C35 30.6602 34.9141 30.8893 34.7422 31.0898C34.5703 31.2617 34.3555 31.3477 34.0977 31.3477H28.5977C28.3398 31.3477 28.1107 31.2617 27.9102 31.0898C27.7383 30.8893 27.6523 30.6602 27.6523 30.4023V21.25C27.6523 20.9922 27.7383 20.7773 27.9102 20.6055C28.1107 20.4336 28.3398 20.3477 28.5977 20.3477H34.0977ZM16.6523 18.5V28.5977H25.8477V31.3477H13V28.5977H14.8477V18.5C14.8477 18.013 15.0195 17.5833 15.3633 17.2109C15.7357 16.8385 16.1654 16.6523 16.6523 16.6523H33.1523V18.5H16.6523Z"
              fill="#908E8E"
              className="group-hover:fill-[#8e2a2f] transition-colors duration-300"
            />
          </g>
          <defs>
            <clipPath id="clip0_125_3578">
              <rect width="48" height="48" fill="white" />
            </clipPath>
          </defs>
        </svg>
      ),
      isMaterialIcon: false,
      onClick: () => navigate('/security'),
      title: 'Security',
      description: 'Your last activity and credentials',
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="group">
          <g clipPath="url(#clip0_125_3585)">
            <rect width="48" height="48" rx="8" fill="white" />
            <rect
              width="48" height="48" rx="8"
              fill="#3A3335"
              fillOpacity="0.04"
              className="group-hover:fill-[#8e2a2f] group-hover:fill-opacity-10 transition-all duration-300"
            />
            <path
              d="M24 13.9023L32.25 17.5977V23.0977C32.25 25.6471 31.4622 27.9818 29.8867 30.1016C28.3112 32.1927 26.349 33.5247 24 34.0977C21.651 33.5247 19.6888 32.1927 18.1133 30.1016C16.5378 27.9818 15.75 25.6471 15.75 23.0977V17.5977L24 13.9023ZM24 24V32.207C25.6901 31.6628 27.1224 30.6458 28.2969 29.1562C29.4714 27.638 30.1732 25.9193 30.4023 24H24ZM24 24V15.9219L17.5977 18.7578V24H24Z"
              fill="#908E8E"
              className="group-hover:fill-[#8e2a2f] transition-colors duration-300"
            />
          </g>
          <defs>
            <clipPath id="clip0_125_3585">
              <rect width="48" height="48" fill="white" />
            </clipPath>
          </defs>
        </svg>
      ),
      isMaterialIcon: false,
      onClick: () => navigate('/change-password-login'),
      title: 'Login details',
      description: 'Password & security questions',
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="group">
          <g clipPath="url(#clip0_125_3571)">
            <rect width="48" height="48" rx="8" fill="white" />
            <rect
              width="48" height="48" rx="8"
              fill="#3A3335"
              fillOpacity="0.04"
              className="group-hover:fill-[#8e2a2f] group-hover:fill-opacity-10 transition-all duration-300"
            />
            <path
              d="M25.2891 29.5859C27.2083 29.2135 28.168 28.2539 28.168 26.707C28.168 24.9596 26.8789 23.7565 24.3008 23.0977C22.6966 22.6966 21.8945 22.1094 21.8945 21.3359C21.8945 20.9062 22.0807 20.5482 22.4531 20.2617C22.8542 19.9753 23.3984 19.832 24.0859 19.832C25.375 19.832 26.0482 20.4049 26.1055 21.5508H27.9102C27.8529 19.918 26.9792 18.8867 25.2891 18.457V16.6523H22.8398V18.457C22.0091 18.6289 21.3216 18.9727 20.7773 19.4883C20.2617 19.9753 20.0039 20.6055 20.0039 21.3789C20.0039 22.9831 21.2786 24.1003 23.8281 24.7305C25.4609 25.1029 26.2773 25.7617 26.2773 26.707C26.2773 27.0794 26.1055 27.4232 25.7617 27.7383C25.4466 28.0247 24.888 28.168 24.0859 28.168C22.5677 28.168 21.7513 27.5951 21.6367 26.4492H19.832C19.918 28.082 20.9206 29.1133 22.8398 29.543V31.3477H25.2891V29.5859ZM17.5117 17.5547C19.3164 15.75 21.4792 14.8477 24 14.8477C26.5208 14.8477 28.6693 15.75 30.4453 17.5547C32.25 19.3307 33.1523 21.4792 33.1523 24C33.1523 26.5208 32.25 28.6836 30.4453 30.4883C28.6693 32.2643 26.5208 33.1523 24 33.1523C21.4792 33.1523 19.3164 32.2643 17.5117 30.4883C15.7357 28.6836 14.8477 26.5208 14.8477 24C14.8477 21.4792 15.7357 19.3307 17.5117 17.5547Z"
              fill="#908E8E"
              className="group-hover:fill-[#8e2a2f] transition-colors duration-300"
            />
          </g>
          <defs>
            <clipPath id="clip0_125_3571">
              <rect width="48" height="48" fill="white" />
            </clipPath>
          </defs>
        </svg>
      ),
      isMaterialIcon: false,
      onClick: () => navigate('/billing-subscription'),
      title: 'Billing & Subscription',
      description: 'Setup payment methods',
    },
    {
      icon: (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="group">
          <g clipPath="url(#clip0_353_2263)">
            <rect width="48" height="48" rx="8" fill="white" />
            <rect
              width="48" height="48" rx="8"
              fill="#3A3335"
              fillOpacity="0.04"
              className="group-hover:fill-[#8e2a2f] group-hover:fill-opacity-10 transition-all duration-300"
            />
            <path
              d="M29.5 27.6523L31.3477 29.5V30.4023H16.6523V29.5L18.5 27.6523V23.0977C18.5 21.6654 18.8581 20.4193 19.5742 19.3594C20.319 18.2995 21.3359 17.612 22.625 17.2969V16.6523C22.625 16.2799 22.7539 15.9648 23.0117 15.707C23.2695 15.4206 23.599 15.2773 24 15.2773C24.401 15.2773 24.7305 15.4206 24.9883 15.707C25.2461 15.9648 25.375 16.2799 25.375 16.6523V17.2969C26.6641 17.612 27.6667 18.2995 28.3828 19.3594C29.1276 20.4193 29.5 21.6654 29.5 23.0977V27.6523ZM24 33.1523C23.4844 33.1523 23.0404 32.9805 22.668 32.6367C22.3242 32.293 22.1523 31.8633 22.1523 31.3477H25.8477C25.8477 31.8346 25.6615 32.2643 25.2891 32.6367C24.9167 32.9805 24.487 33.1523 24 33.1523Z"
              fill="#908E8E"
              className="group-hover:fill-[#8e2a2f] transition-colors duration-300"
            />
          </g>
          <defs>
            <clipPath id="clip0_353_2263">
              <rect width="48" height="48" fill="white" />
            </clipPath>
          </defs>
        </svg>
      ),
      isMaterialIcon: false,
      onClick: () => navigate('/notifications'),
      title: 'Notifications',
      description: 'Newsletter & Promo',
    },
    // Logout menu item removed 2026-06-04 — the global header already
    // exposes Logout, and duplicating it here was creating two
    // confirmation paths for the same action.
    {
      icon: 'download',
      isMaterialIcon: true,
      onClick: handleExportData,
      title: 'Download my data',
      description: 'Get a copy of your personal data',
    },
    {
      icon: 'delete_forever',
      isMaterialIcon: true,
      onClick: () => setDeleteOpen(true),
      title: 'Delete account',
      description: 'Permanently erase your account & data',
    },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-12">
        {/* Breadcrumb & Tab */}
        <div className="flex items-center justify-between mb-12">
          <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px]">
            Account Settings
          </h2>
        </div>

        {/* Profile Info */}
        <div className="flex flex-col items-center mb-16">
          <div className="w-[114px] h-[114px] rounded-[20px] overflow-hidden mb-6 bg-[#f2eee2] flex items-center justify-center">
            {profilePic ? (
              <img
                src={profilePic}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[44px]">
                {(user?.first_name ?? user?.email ?? '?')
                  .slice(0, 1)
                  .toUpperCase()}
              </span>
            )}
          </div>
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[24px] leading-[36px] tracking-[-0.08px] mb-1">
            {displayName}
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
            {displayEmail}
          </p>
        </div>

        {/* Settings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-24">
          {settingsCards.map((card, index) => (
            <SettingsCard
              key={index}
              icon={card.icon}
              title={card.title}
              description={card.description}
              isMaterialIcon={card.isMaterialIcon}
              onClick={card.onClick}
              to={card.to}
              showArrow={card.showArrow}
              className=""
            />
          ))}
        </div>
      </main>

      <Footer />

      <ConfirmModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={performDelete}
        title="Delete your account?"
        message="This permanently erases your personal data and can't be undone. Past booking records are kept (anonymised) where the law requires it."
        confirmLabel="Yes, delete my account"
        cancelLabel="Cancel"
        confirming={deleting}
      />
    </div>
  );
}
